import { checkSupportingDocument } from '../../contractor-payments/fileCheck';

export const MAX_OUTSIDE_FILES = 5;

/** Per-file states. A file is checked in the browser first (a convenience: the server repeats every check), then sent with the save, which stores every file
 * before it records the payment, so a file is only ever `success` after the server has saved the record. */
export type FileState = 'checking' | 'ready' | 'invalid' | 'uploading' | 'success' | 'failed';

export interface FileEntry {
  id: string;
  file: File;
  state: FileState;
  message: string | null;
}

export type FileAction =
  | { type: 'add'; entries: FileEntry[] }
  | { type: 'checked'; id: string; ok: boolean; message: string | null }
  | { type: 'remove'; id: string }
  | { type: 'upload-started' }
  | { type: 'upload-succeeded' }
  | { type: 'upload-failed'; message: string };

export function filesReducer(state: FileEntry[], action: FileAction): FileEntry[] {
  switch (action.type) {
    case 'add':
      return [...state, ...action.entries];
    case 'checked':
      return state.map((e) => (e.id === action.id && e.state === 'checking' ? { ...e, state: action.ok ? 'ready' : 'invalid', message: action.ok ? null : action.message } : e));
    case 'remove':
      return state.filter((e) => e.id !== action.id);
    case 'upload-started':
      return state.map((e) => (e.state === 'ready' || e.state === 'failed' ? { ...e, state: 'uploading', message: null } : e));
    case 'upload-succeeded':
      return state.map((e) => (e.state === 'uploading' ? { ...e, state: 'success', message: null } : e));
    case 'upload-failed':
      return state.map((e) => (e.state === 'uploading' ? { ...e, state: 'failed', message: action.message } : e));
    default:
      return state;
  }
}

/** Save needs every attached file checked and valid. A file that failed to upload may be sent again (retry); one that is invalid or still being checked blocks Save. */
export function filesAllowSave(entries: FileEntry[]): boolean {
  return entries.every((e) => e.state === 'ready' || e.state === 'failed');
}

export function filesBlockingSave(entries: FileEntry[]): FileEntry[] {
  return entries.filter((e) => e.state === 'checking' || e.state === 'invalid' || e.state === 'uploading');
}

let counter = 0;
export function newFileEntry(file: File): FileEntry {
  counter += 1;
  return { id: `file-${counter}`, file, state: 'checking', message: null };
}

/** Takes the files the person picked: refuses a sixth file or a repeat, and starts each other file's check. Returns the entries to add and any refusals to show. */
export function admitFiles(existing: FileEntry[], picked: File[]): { entries: FileEntry[]; refused: string[] } {
  const entries: FileEntry[] = [];
  const refused: string[] = [];
  for (const file of picked) {
    if (existing.length + entries.length >= MAX_OUTSIDE_FILES) {
      refused.push(`${file.name}: at most ${MAX_OUTSIDE_FILES} files can be attached.`);
    } else if ([...existing, ...entries].some((e) => e.file.name === file.name && e.file.size === file.size)) {
      refused.push(`${file.name}: this file is already attached.`);
    } else {
      entries.push(newFileEntry(file));
    }
  }
  return { entries, refused };
}

export async function checkEntry(entry: FileEntry): Promise<{ ok: boolean; message: string | null }> {
  const result = await checkSupportingDocument(entry.file);
  return result.ok ? { ok: true, message: null } : { ok: false, message: result.message };
}
